import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChatCircle,
  CheckCircle,
  CalendarCheck,
  ArrowRight,
  MessengerLogo,
  InstagramLogo,
  TrendUp,
  Clock,
  Lightning,
  Circle
} from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

function WhatsAppIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function WebchatIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C6.48 2 2 6.03 2 11c0 2.7 1.22 5.12 3.17 6.8L4 22l4.64-2.02A10.64 10.64 0 0 0 12 20c5.52 0 10-4.03 10-9S17.52 2 12 2z"/>
      <circle cx="8" cy="11" r="1.2" fill="white"/>
      <circle cx="12" cy="11" r="1.2" fill="white"/>
      <circle cx="16" cy="11" r="1.2" fill="white"/>
    </svg>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KPICard({ icon: Icon, title, value, subtitle, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    amber: 'text-amber-600 bg-amber-50',
    violet: 'text-violet-600 bg-violet-50',
    rose: 'text-rose-600 bg-rose-50',
  };

  return (
    <Card className="bg-white border-zinc-200 hover:border-zinc-300 transition-colors duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon size={24} weight="duotone" />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
              <TrendUp size={16} />
              {trend}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-heading font-bold text-zinc-900">{value}</p>
          <p className="text-sm text-zinc-500 mt-1">{title}</p>
          {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniBarChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-24">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-blue-500 rounded-t-sm transition-all duration-500"
            style={{ height: `${Math.max((d.count / max) * 80, d.count > 0 ? 4 : 0)}px` }}
          />
          <span className="text-[10px] text-zinc-400">{d.date}</span>
        </div>
      ))}
    </div>
  );
}

function StatusBar({ status_breakdown }) {
  const total = (status_breakdown?.active || 0) + (status_breakdown?.pending || 0) + (status_breakdown?.resolved || 0);
  if (total === 0) return <div className="text-sm text-zinc-400">Ingen data endnu</div>;

  const pct = (n) => Math.round((n / total) * 100);
  const active = status_breakdown?.active || 0;
  const pending = status_breakdown?.pending || 0;
  const resolved = status_breakdown?.resolved || 0;

  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
        {active > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${pct(active)}%` }} />}
        {pending > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${pct(pending)}%` }} />}
        {resolved > 0 && <div className="bg-zinc-300 transition-all" style={{ width: `${pct(resolved)}%` }} />}
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="text-zinc-600">Aktive</span>
          <span className="font-semibold text-zinc-900 ml-1">{active}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="text-zinc-600">Afventer</span>
          <span className="font-semibold text-zinc-900 ml-1">{pending}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
          <span className="text-zinc-600">Løst</span>
          <span className="font-semibold text-zinc-900 ml-1">{resolved}</span>
        </div>
      </div>
    </div>
  );
}

function ConversationItem({ conversation }) {
  const PlatformIcon = conversation.platform === 'messenger' ? MessengerLogo : conversation.platform === 'whatsapp' ? WhatsAppIcon : conversation.platform === 'webchat' ? WebchatIcon : InstagramLogo;

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    resolved: 'bg-zinc-100 text-zinc-600',
    pending: 'bg-amber-100 text-amber-700',
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Lige nu';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m siden`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}t siden`;
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="flex items-center gap-4 p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        conversation.platform === 'messenger' ? 'bg-blue-100' : conversation.platform === 'whatsapp' ? 'bg-green-100' : conversation.platform === 'webchat' ? 'bg-sky-100' : 'bg-gradient-to-br from-purple-100 to-pink-100'
      }`}>
        <PlatformIcon
          size={20}
          weight={conversation.platform === 'whatsapp' || conversation.platform === 'webchat' ? undefined : 'fill'}
          className={conversation.platform === 'messenger' ? 'text-blue-600' : conversation.platform === 'whatsapp' ? 'text-[#25D366]' : conversation.platform === 'webchat' ? 'text-sky-500' : 'text-purple-600'}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-zinc-900 truncate">
            {conversation.user_name || `User ${conversation.user_id.slice(-6)}`}
          </span>
          <Badge className={`${statusColors[conversation.status]} text-xs px-1.5 py-0`}>
            {conversation.status}
          </Badge>
        </div>
        <p className="text-sm text-zinc-500 truncate mt-0.5">
          {conversation.last_message || 'Ingen beskeder endnu'}
        </p>
      </div>
      <div className="text-xs text-zinc-400 flex-shrink-0">
        {formatTime(conversation.last_message_time)}
      </div>
    </div>
  );
}

export function Dashboard() {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, conversationsRes] = await Promise.all([
          axios.get(`${API}/analytics`),
          axios.get(`${API}/conversations?limit=6`)
        ]);
        setAnalytics(analyticsRes.data);
        setConversations(conversationsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const formatResponseTime = (secs) => {
    if (!secs || secs === 0) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.round(secs / 60)}m`;
  };

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold text-zinc-900 tracking-tight">
            {t('dashboard')}
          </h1>
          <p className="text-zinc-500 mt-1">Live chat & AI — samlet overblik</p>
        </div>
        {analytics?.active_now > 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full">
            <Circle size={10} weight="fill" className="animate-pulse" />
            {analytics.active_now} aktiv{analytics.active_now !== 1 ? 'e' : ''} samtale{analytics.active_now !== 1 ? 'r' : ''} nu
          </div>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={ChatCircle}
          title="Samtaler i alt"
          value={analytics?.total_conversations || 0}
          subtitle={`${analytics?.conversations_today || 0} i dag`}
          color="blue"
        />
        <KPICard
          icon={CalendarCheck}
          title="Bookinger oprettet"
          value={analytics?.appointments_booked || 0}
          subtitle="via GECKO"
          color="indigo"
        />
        <KPICard
          icon={CheckCircle}
          title="Løsningsrate"
          value={`${analytics?.ai_resolution_rate || 0}%`}
          subtitle={`${analytics?.status_breakdown?.resolved || 0} løste samtaler`}
          color="emerald"
        />
        <KPICard
          icon={Lightning}
          title="Gns. svartid"
          value={formatResponseTime(analytics?.avg_response_time_seconds)}
          subtitle="Bot → kunde"
          color="amber"
        />
      </div>

      {/* Middle row: Messages chart + Status breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Messages per day chart */}
        <Card className="lg:col-span-2 bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-zinc-900">Beskeder de seneste 7 dage</h2>
              <span className="text-sm text-zinc-400">{analytics?.total_messages || 0} i alt</span>
            </div>
            <MiniBarChart data={analytics?.messages_per_day} />
          </CardContent>
        </Card>

        {/* Status breakdown */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-zinc-900 mb-4">Samtale-status</h2>
            <StatusBar status_breakdown={analytics?.status_breakdown} />
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Clock size={14} />
                <span>{analytics?.messages_today || 0} beskeder i dag</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: Recent conversations + Platform breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-zinc-900">{t('recentConversations')}</h2>
              <Link to="/conversations">
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                  {t('viewAll')}
                  <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              {conversations.length > 0 ? (
                conversations.map((conv) => (
                  <ConversationItem key={conv.id} conversation={conv} />
                ))
              ) : (
                <div className="p-8 text-center text-zinc-500">
                  <ChatCircle size={48} className="mx-auto mb-3 text-zinc-300" />
                  <p>{t('noConversations')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Platform breakdown */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-zinc-900 mb-4">Platform Breakdown</h2>
            <div className="space-y-3">
              {[
                { platform: 'messenger', label: 'Messenger', Icon: MessengerLogo, bg: 'bg-blue-50',   color: 'text-blue-600',    bar: 'bg-blue-500' },
                { platform: 'instagram', label: 'Instagram', Icon: InstagramLogo, bg: 'bg-purple-50', color: 'text-purple-600',  bar: 'bg-purple-500' },
                { platform: 'whatsapp',  label: 'WhatsApp',  Icon: WhatsAppIcon,  bg: 'bg-green-50',  color: 'text-[#25D366]',   bar: 'bg-[#25D366]' },
                { platform: 'webchat',   label: 'Webchat',   Icon: WebchatIcon,   bg: 'bg-sky-50',    color: 'text-sky-500',     bar: 'bg-sky-500' },
              ].map(({ platform, label, Icon, bg, color, bar }) => {
                const count = analytics?.platform_breakdown?.[platform] || 0;
                const total = Object.values(analytics?.platform_breakdown || {}).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={platform} className={`p-3 rounded-lg ${bg}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon size={18} weight={platform === 'whatsapp' ? undefined : 'fill'} className={color} />
                        <span className="text-sm font-medium text-zinc-900">{label}</span>
                      </div>
                      <span className="font-bold text-zinc-900">{count}</span>
                    </div>
                    <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                      <div className={`h-full ${bar} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">{pct}% af samtaler</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
